import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { StatusTag } from './StatusTag';
import { CLIENT_STAGE_ORDER, metaLabel, stageHeading } from '../utils/deliverables';
import type { Deliverable } from '../types/portal';
function isOpenable(item: Deliverable): boolean {
  // A transcript opens as soon as Portal has uploaded one, whatever stage it sits in.
  if (item.type === 'Transcript') return Boolean(item.transcript?.trim());
  return item.stage === 'Review' && item.status !== 'Not Started' && item.status !== 'In Production';
}

/**
 * Every deliverable Portal has marked visible, grouped by the stage it is in —
 * planned work included, without actions it does not yet have.
 */
export function DeliverableGroups() {
  const {
    clientDeliverables,
    openDeliverable
  } = usePortal();
  if (clientDeliverables.length === 0) {
    return <p className="border-b border-rule py-8 font-display text-xl text-mute" data-id="element-77" data-mp="1d94p1g79fb5j">
        Deliverables are being confirmed.
      </p>;
  }
  const groups = CLIENT_STAGE_ORDER.map(stage => ({
    stage,
    items: clientDeliverables.filter(item => item.stage === stage)
  })).filter(group => group.items.length > 0);
  return <div data-id="element-78" data-mp="1sllgscn19w0u">
      {groups.map((group, groupIndex) => <section key={group.stage} className={groupIndex === 0 ? '' : 'mt-12'} data-id="element-79" data-mp="1nn0gkd7s7cce" data-mpi={groupIndex}>
          <h3 className="label border-b border-rule pb-3 text-mute" data-id="element-80" data-mp="2kc9fzj55crbv">
            {stageHeading(group.stage)}
          </h3>
          <ul className="m-0 list-none p-0" data-id="element-81" data-mp="2gbyzvdnn4kja">
            {group.items.map((item, index) => {
          const openable = isOpenable(item);
          return <li key={item.id} className="border-b border-rule" data-id="element-82" data-mp="26lcqi16ioq91" data-mpi={index}>
                  <button type="button" disabled={!openable} onClick={() => openDeliverable(item.id)} className={['group grid w-full grid-cols-[3.5rem_1fr] items-baseline gap-x-5 gap-y-4 py-7 text-left sm:grid-cols-[4.5rem_1fr_auto] sm:gap-x-8', openable ? 'cursor-pointer' : 'cursor-default'].join(' ')} data-id="element-83" data-mp="26j0b9vs58tzo">
                    <span className="font-display text-3xl leading-none text-mute sm:text-4xl" data-id="element-84" data-mp="39cxgfdxy5bvh">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="min-w-0" data-id="element-85" data-mp="2zf9gm9lhhe6g">
                      <span className={['block font-display text-2xl leading-tight transition-colors duration-150 ease-out sm:text-4xl', openable ? 'text-ink group-hover:text-graphite' : 'text-graphite'].join(' ')} data-id="element-86" data-mp="2zhlzcoz2fskk">
                        {item.name}
                      </span>
                      <span className="label-sm mt-3 block text-mute" data-id="element-87" data-mp="oln7tdir6b5z">{metaLabel(item)}</span>
                    </span>
                    <span className="col-start-2 flex items-center gap-4 sm:col-start-3" data-id="element-88" data-mp="2d6ubj2dcm7e9">
                      <StatusTag status={item.status} data-id="element-89" data-mp="295imaxydzo6l" />
                      {openable && <ArrowRightIcon className="hidden h-3.5 w-3.5 text-mute transition-transform duration-150 ease-out group-hover:translate-x-1 sm:block" strokeWidth={1.25} data-id="element-90" data-mp="2mwx1sut3578w" />}
                    </span>
                  </button>
                </li>;
        })}
          </ul>
        </section>)}
    </div>;
}